import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useIssuers, useNetworkStats } from '../api/hooks';
import { useAuth } from '../auth/AuthContext';
import ZKAnchorTimeline from '../components/ZKAnchorTimeline';

type SortKey = 'volume' | 'revocation' | 'recent';

/** Autonomy ladder, in ladder order. Keys match the integer levels the API sends. */
const LEVELS = [
  { level: '0', key: 'intern', bar: 'bg-slate-400' },
  { level: '1', key: 'junior', bar: 'bg-sky-500' },
  { level: '2', key: 'senior', bar: 'bg-emerald-500' },
  { level: '3', key: 'principal', bar: 'bg-violet-500' },
] as const;

function shortDid(did: string) {
  return did.length > 28 ? `${did.slice(0, 18)}…${did.slice(-6)}` : did;
}

function formatDate(value: string | null) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString();
}

export default function TrustExplorer() {
  const [sortKey, setSortKey] = useState<SortKey>('volume');
  const [copied, setCopied] = useState<string | null>(null);
  const { data: issuers, loading: issuersLoading, error: issuersError } = useIssuers();
  const { data: stats, loading: statsLoading, error: statsError } = useNetworkStats();
  const { hasRole } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation(['trust', 'common']);

  const issuerList = useMemo(() => (Array.isArray(issuers) ? issuers : []), [issuers]);

  const sorted = useMemo(() => {
    const rows = [...issuerList];
    if (sortKey === 'volume') {
      rows.sort((a, b) => b.totalIssued - a.totalIssued);
    } else if (sortKey === 'revocation') {
      const rate = (r: typeof rows[number]) => (r.totalIssued > 0 ? r.revoked / r.totalIssued : 0);
      rows.sort((a, b) => rate(b) - rate(a));
    } else {
      rows.sort((a, b) => (b.lastIssued ?? '').localeCompare(a.lastIssued ?? ''));
    }
    return rows;
  }, [issuerList, sortKey]);

  // Export everything the page shows — the summary cards, the agent trust
  // distribution and the issuer table (in its current sort order) — so the
  // file stands on its own as an offline record of the page.
  const handleExport = () => {
    const exportedAt = new Date().toISOString();
    const snapshot = {
      exportedAt,
      summary: stats
        ? {
            totalDIDs: stats.totalDIDs,
            totalIssuers: stats.totalIssuers,
            totalCredentials: stats.totalCredentials,
            totalRevoked: stats.totalRevoked,
            revocationRatePercent: stats.revocationRate,
            totalSchemas: stats.totalSchemas,
            totalAgents: stats.totalAgents,
            totalZKProofs: stats.totalZKProofs,
          }
        : null,
      agentTrust: stats
        ? {
            totalAgents,
            scoredAgents: stats.scoredAgents,
            trustSignals: stats.trustSignals,
            agentsByAutonomyLevel: Object.fromEntries(
              LEVELS.map(({ level, key }) => [key, stats.agentsByLevel?.[level] ?? 0]),
            ),
          }
        : null,
      issuers: {
        sortedBy: sortKey,
        count: sorted.length,
        rows: sorted.map((i) => ({
          did: i.did,
          name: i.name && i.name !== i.did ? i.name : null,
          totalIssued: i.totalIssued,
          active: i.active,
          revoked: i.revoked,
          revocationRatePercent: i.totalIssued > 0 ? Math.round((i.revoked / i.totalIssued) * 1000) / 10 : 0,
          firstIssued: i.firstIssued,
          lastIssued: i.lastIssued,
        })),
      },
    };
    const blob = new Blob([JSON.stringify(snapshot, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ida-trust-explorer-${exportedAt.slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const copyDID = (did: string) => {
    navigator.clipboard.writeText(did);
    setCopied(did);
    setTimeout(() => setCopied(null), 2000);
  };

  const totalAgents = stats?.totalAgents ?? 0;

  const cards = stats
    ? [
        { key: 'dids', value: String(stats.totalDIDs), icon: 'badge' },
        { key: 'issuers', value: String(stats.totalIssuers), icon: 'verified_user' },
        { key: 'credentials', value: String(stats.totalCredentials), icon: 'verified' },
        { key: 'revocationRate', value: `${stats.revocationRate}%`, icon: 'gpp_bad' },
        { key: 'agents', value: String(stats.totalAgents), icon: 'smart_toy' },
        { key: 'zkProofs', value: String(stats.totalZKProofs), icon: 'shield_lock' },
      ]
    : [];

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-text-main">{t('title')}</h1>
          <p className="mt-1 text-sm text-text-muted">{t('subtitle')}</p>
          <p className="mt-1 text-xs text-text-faint">{t('scopeNote')}</p>
        </div>
        <div className="flex gap-3">
          <button className="btn-secondary" onClick={handleExport}>
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>download</span>
            {t('exportData')}
          </button>
          {/* hasRole('issuer') is true for admins too; admins don't onboard. */}
          {hasRole('issuer') && !hasRole('admin') && (
            <button className="btn-primary" onClick={() => navigate('/issuer/onboarding')}>
              <span className="material-symbols-outlined" style={{ fontSize: 18 }}>add_circle</span>
              {t('registerIssuer')}
            </button>
          )}
        </div>
      </div>

      {statsLoading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : statsError ? (
        <div className="py-20 text-center text-red-500">{t('common:errors.loadStatsFailed')}</div>
      ) : (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-6">
          {cards.map((card) => (
            <div key={card.key} className="stat-card group h-28">
              <span className="text-[10px] font-bold uppercase tracking-wider text-text-muted">
                {t(`stats.${card.key}`)}
              </span>
              <div className="mt-auto">
                <span className="text-2xl font-bold tabular-nums text-text-main">{card.value}</span>
              </div>
              <span
                className="material-symbols-outlined absolute -right-2 -top-2 text-slate-900 opacity-5 transition-opacity group-hover:opacity-10"
                style={{ fontSize: 80, fontVariationSettings: "'FILL' 1" }}
              >
                {card.icon}
              </span>
            </div>
          ))}
        </div>
      )}

      {stats && (
        <section className="rounded-xl border border-border-subtle bg-white p-6 shadow-sm">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="text-sm font-bold tracking-tight text-text-main">{t('trust.heading')}</h2>
            <span className="text-xs text-text-faint">
              {t('trust.signals', { count: stats.trustSignals })}
            </span>
          </div>
          <p className="mt-1 text-xs text-text-muted">
            {t('trust.subheading', { scored: stats.scoredAgents, total: totalAgents })}
          </p>

          {totalAgents === 0 ? (
            <p className="mt-6 text-sm text-text-faint">{t('trust.noData')}</p>
          ) : (
            <div className="mt-5 space-y-3">
              {LEVELS.map(({ level, key, bar }) => {
                const count = stats.agentsByLevel?.[level] ?? 0;
                const pct = totalAgents > 0 ? (count / totalAgents) * 100 : 0;
                return (
                  <div key={level} className="flex items-center gap-3">
                    <span className="w-20 shrink-0 text-xs font-bold text-text-muted">
                      {t(`trust.level.${key}`)}
                    </span>
                    <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100">
                      <div className={`h-full rounded-full ${bar}`} style={{ width: `${pct}%` }} />
                    </div>
                    <span className="w-10 shrink-0 text-right text-xs font-bold tabular-nums text-text-main">
                      {count}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      )}

      <div className="sticky top-16 z-[5] flex items-center justify-end rounded-xl border border-border-subtle bg-white p-3">
        <div className="flex items-center gap-2">
          {(['volume', 'revocation', 'recent'] as SortKey[]).map((key) => (
            <button
              key={key}
              onClick={() => setSortKey(key)}
              className={`filter-pill whitespace-nowrap ${sortKey === key ? 'active' : ''}`}
            >
              {t(`sort.${key}`)}
            </button>
          ))}
        </div>
      </div>

      {issuersLoading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : issuersError ? (
        <div className="py-20 text-center text-red-500">{t('common:errors.loadIssuersFailed')}</div>
      ) : (
        <div className="grid gap-6 xl:grid-cols-[2fr_1fr]">
          <div className="overflow-hidden rounded-xl border border-border-subtle bg-white shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border-subtle bg-slate-50/50">
                    <th className="px-6 py-3 text-left text-[11px] font-extrabold uppercase tracking-widest text-text-faint">{t('table.issuer')}</th>
                    <th className="px-6 py-3 text-left text-[11px] font-extrabold uppercase tracking-widest text-text-faint">{t('table.issued')}</th>
                    <th className="px-6 py-3 text-left text-[11px] font-extrabold uppercase tracking-widest text-text-faint">{t('table.revocation')}</th>
                    <th className="px-6 py-3 text-left text-[11px] font-extrabold uppercase tracking-widest text-text-faint">{t('table.lastActive')}</th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-6 py-16 text-center text-sm text-text-faint">
                        {t('table.empty')}
                      </td>
                    </tr>
                  ) : (
                    sorted.map((issuer) => {
                      const rate = issuer.totalIssued > 0 ? (issuer.revoked / issuer.totalIssued) * 100 : 0;
                      // A high revocation rate is the clearest trust signal this
                      // data supports, so it is shown as a measured number rather
                      // than a verified/pending badge with nothing behind it.
                      const tone =
                        rate >= 50 ? 'text-red-600' : rate > 0 ? 'text-amber-600' : 'text-emerald-600';
                      const named = issuer.name && issuer.name !== issuer.did;
                      const lastIssued = formatDate(issuer.lastIssued);
                      return (
                        <tr key={issuer.did} className="border-b border-border-subtle transition-colors hover:bg-slate-50/60">
                          <td className="px-6 py-4">
                            <p className="text-sm font-bold tracking-tight text-text-main">
                              {named ? issuer.name : t('table.unnamed')}
                            </p>
                            <div className="mt-1 flex items-center gap-1">
                              <span className="font-mono text-[11px] text-text-faint">{shortDid(issuer.did)}</span>
                              <button
                                onClick={() => copyDID(issuer.did)}
                                className="text-text-faint transition-colors hover:text-primary"
                                aria-label={issuer.did}
                              >
                                <span className="material-symbols-outlined" style={{ fontSize: 14 }}>
                                  {copied === issuer.did ? 'check' : 'content_copy'}
                                </span>
                              </button>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm font-bold tabular-nums text-text-main">{issuer.totalIssued}</span>
                            <p className="text-[11px] text-text-faint">{t('table.active', { count: issuer.active })}</p>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`text-sm font-bold tabular-nums ${tone}`}>{rate.toFixed(0)}%</span>
                            <p className="text-[11px] text-text-faint">{t('table.revoked', { count: issuer.revoked })}</p>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-xs text-text-muted">{lastIssued ?? t('table.never')}</span>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between border-t border-border-subtle px-6 py-3">
              <span className="text-xs text-text-faint">{t('table.showing', { count: sorted.length })}</span>
            </div>
          </div>

          {/* Phase-7-A: ZK anchor activity timeline (right rail). */}
          <div className="space-y-6">
            <ZKAnchorTimeline
              centralDid={sorted[0]?.did}
              limit={20}
              enableKindFilter
              heading={t('zkTimeline.heading')}
            />
          </div>
        </div>
      )}
    </div>
  );
}
